import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Snackbar,
  alpha,
  TablePagination,
  Divider,
  Stack,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlightIcon from '@mui/icons-material/Flight';
import TrainIcon from '@mui/icons-material/Train';
import HotelIcon from '@mui/icons-material/Hotel';
import API from '../../api/axios';

const statusColors = {
  Pending: { color: '#f59e0b', bg: alpha('#f59e0b', 0.1) },
  Confirmed: { color: '#3b82f6', bg: alpha('#3b82f6', 0.1) },
  Completed: { color: '#10b981', bg: alpha('#10b981', 0.1) },
  Cancelled: { color: '#ef4444', bg: alpha('#ef4444', 0.1) },
};

const StatusChip = ({ status }) => {
  const colors = statusColors[status] || { color: '#6b7280', bg: alpha('#6b7280', 0.1) };
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontWeight: 600,
        color: colors.color,
        bgcolor: colors.bg,
        border: `1px solid ${alpha(colors.color, 0.2)}`,
        borderRadius: 1.5,
      }}
    />
  );
};

const ManageBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Confirm delete dialog
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Booking detail dialog
  const [detailBooking, setDetailBooking] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const getServiceIcon = (type) => {
    if (!type) return null;
    const t = type.toLowerCase();
    if (t.includes('flight')) return <FlightIcon sx={{ fontSize: 20, color: '#6366f1' }} />;
    if (t.includes('railway') || t.includes('train')) return <TrainIcon sx={{ fontSize: 20, color: '#f59e0b' }} />;
    if (t.includes('hotel')) return <HotelIcon sx={{ fontSize: 20, color: '#10b981' }} />;
    return null;
  };

  const openDetailDialog = (booking) => {
    setDetailBooking(booking);
    setDetailDialogOpen(true);
  };
  const closeDetailDialog = () => {
    setDetailBooking(null);
    setDetailDialogOpen(false);
  };

  useEffect(() => { fetchBookings(); }, []);

  const fetchBookings = async () => {
    try {
      const { data } = await API.get('/bookings');
      setBookings(data);
    } catch (err) {
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await API.put(`/bookings/${bookingId}`, { status: newStatus });
      setBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, status: newStatus } : b)));
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const openDeleteDialog = (id) => { setDeleteId(id); setDeleteDialogOpen(true); };
  const closeDeleteDialog = () => { setDeleteId(null); setDeleteDialogOpen(false); };

  const handleDelete = async () => {
    try {
      await API.delete(`/bookings/${deleteId}`);
      setBookings((prev) => prev.filter((b) => b.id !== deleteId));
      setSnackbar({ open: true, message: 'Booking deleted successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete booking', severity: 'error' });
    } finally {
      closeDeleteDialog();
    }
  };

  const filteredBookings = bookings.filter((b) => {
    const matchesSearch =
      !search ||
      b.user_name?.toLowerCase().includes(search.toLowerCase()) ||
      b.email?.toLowerCase().includes(search.toLowerCase()) ||
      b.service_type?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'All' || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const paginatedBookings = filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
          Manage Bookings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage all customer bookings
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name, email, service..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ flexGrow: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          startAdornment={<FilterListIcon sx={{ fontSize: 18, ml: 1, mr: 0.5, color: 'text.secondary' }} />}
          sx={{ minWidth: 160 }}
        >
          {['All', 'Pending', 'Confirmed', 'Completed', 'Cancelled'].map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </Select>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                {['Customer', 'Contact', 'Service', 'Date', 'People', 'Meal', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    {search || statusFilter !== 'All' ? 'No bookings match your filters.' : 'No bookings yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedBookings.map((booking) => (
                  <TableRow
                    key={booking.id}
                    sx={{ '&:hover': { bgcolor: alpha('#667eea', 0.03) }, transition: 'background 0.2s' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{booking.user_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{booking.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{booking.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {booking.service_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {booking.booking_date ? new Date(booking.booking_date).toLocaleDateString('en-IN') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{booking.number_of_people || 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{booking.meal_preference}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={booking.status} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          title="View Details"
                          onClick={() => openDetailDialog(booking)}
                          sx={{ color: '#6366f1', '&:hover': { bgcolor: alpha('#6366f1', 0.1) } }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <Select
                          size="small"
                          value={booking.status}
                          onChange={(e) => handleStatusUpdate(booking.id, e.target.value)}
                          sx={{ fontSize: '0.8rem', minWidth: 110 }}
                        >
                          {['Pending', 'Confirmed', 'Completed', 'Cancelled'].map((s) => (
                            <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>{s}</MenuItem>
                          ))}
                        </Select>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(booking.id)}
                          sx={{ color: '#ef4444', '&:hover': { bgcolor: alpha('#ef4444', 0.1) } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredBookings.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
        />
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Delete Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to permanently delete this booking? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteDialog} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            sx={{ borderRadius: 2, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, boxShadow: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={closeDetailDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {detailBooking && getServiceIcon(detailBooking.service_type)}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Booking Details</Typography>
            <Typography variant="caption" color="text.secondary">ID: #{detailBooking?.id}</Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          {detailBooking && (
            <Stack spacing={2.5}>
              {/* Customer Info */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                  Customer Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body2" fontWeight={600}>{detailBooking.user_name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2">{detailBooking.email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                    <Typography variant="body2">{detailBooking.phone}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Booking Date</Typography>
                    <Typography variant="body2">{detailBooking.booking_date ? new Date(detailBooking.booking_date).toLocaleDateString('en-IN') : '—'}</Typography>
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Service Info */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                  Service Information
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Type</Typography>
                    <Typography variant="body2" fontWeight={600}>{detailBooking.service_type}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}><StatusChip status={detailBooking.status} /></Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Number of People</Typography>
                    <Typography variant="body2">{detailBooking.number_of_people || 1}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Meal Preference</Typography>
                    <Typography variant="body2">{detailBooking.meal_preference}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Flight / Railway specific details */}
              {(detailBooking.from_location || detailBooking.to_location) && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                      {detailBooking.service_type?.toLowerCase().includes('flight') ? 'Flight Details' : 'Railway Details'}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">From</Typography>
                        <Typography variant="body2" fontWeight={600}>{detailBooking.from_location}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">To</Typography>
                        <Typography variant="body2" fontWeight={600}>{detailBooking.to_location}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Class</Typography>
                        <Typography variant="body2">{detailBooking.travel_class}</Typography>
                      </Box>
                      {detailBooking.return_date && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Return Date</Typography>
                          <Typography variant="body2">{new Date(detailBooking.return_date).toLocaleDateString('en-IN')}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </>
              )}

              {/* Hotel specific details */}
              {detailBooking.hotel_city && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                      Hotel Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">City</Typography>
                        <Typography variant="body2" fontWeight={600}>{detailBooking.hotel_city}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Room Type</Typography>
                        <Typography variant="body2">{detailBooking.room_type}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Check-in</Typography>
                        <Typography variant="body2">{detailBooking.check_in_date ? new Date(detailBooking.check_in_date).toLocaleDateString('en-IN') : '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Check-out</Typography>
                        <Typography variant="body2">{detailBooking.check_out_date ? new Date(detailBooking.check_out_date).toLocaleDateString('en-IN') : '—'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Rooms</Typography>
                        <Typography variant="body2">{detailBooking.rooms || 1}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {/* Special Requirements */}
              {detailBooking.special_requirements && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                      Special Requirements
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detailBooking.special_requirements}</Typography>
                  </Box>
                </>
              )}

              {/* Tour Package Info */}
              {detailBooking.tour_package_title && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>
                      Tour Package
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>{detailBooking.tour_package_title}</Typography>
                    {detailBooking.tour_package_category && (
                      <Typography variant="caption" color="text.secondary">Category: {detailBooking.tour_package_category}</Typography>
                    )}
                  </Box>
                </>
              )}

              {/* Timestamps */}
              <Divider />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{detailBooking.created_at ? new Date(detailBooking.created_at).toLocaleString('en-IN') : '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Updated</Typography>
                  <Typography variant="body2">{detailBooking.updated_at ? new Date(detailBooking.updated_at).toLocaleString('en-IN') : '—'}</Typography>
                </Box>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDetailDialog} variant="contained" sx={{ borderRadius: 2, boxShadow: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageBookings;